/**
 * RECUPERAR LA CONTRASEÑA, en dos pantallas que son la misma ruta.
 *
 * Sin token en la URL: el formulario para pedir el enlace.
 * Con token: el formulario para elegir la contraseña nueva.
 *
 * Van juntas porque son un solo trámite visto por el usuario, y separarlas
 * obligaría a explicar en cuál está.
 *
 * LO QUE NO SE DICE NUNCA
 *
 * Si la cuenta existe. La respuesta es la misma en los dos casos, y el texto
 * está escrito en condicional a propósito: "si esa cuenta existe...". Decir lo
 * contrario permitiría averiguar quién se atiende en este policlínico probando
 * direcciones de correo.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, MailCheck } from "lucide-react";
import { confirmarRecuperacion, pedirRecuperacion } from "../api/auth";
import { formatApiError } from "../api/client";
import { useBase } from "../auth/TenantBrandingProvider";
import { Alert, Button, Card, CardBody, Field, Input } from "../components/ui";

export function RecuperarPage() {
  const { token } = useParams<{ token?: string }>();
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-10">
      <Card>
        <CardBody className="space-y-5 p-6">
          {token ? <ElegirNueva token={token} /> : <PedirEnlace />}
        </CardBody>
      </Card>
    </div>
  );
}

function PedirEnlace() {
  const base = useBase();
  const [identificador, setIdentificador] = useState("");

  const pedir = useMutation({
    mutationFn: () => pedirRecuperacion(identificador.trim()),
  });

  if (pedir.isSuccess) {
    return (
      <div className="space-y-4 text-center">
        <MailCheck className="mx-auto h-10 w-10 text-[color:var(--color-link)]" />
        <h1 className="text-lg font-semibold text-slate-900">Revisa tu correo</h1>
        <p className="text-sm text-slate-600">{pedir.data.mensaje}</p>
        <p className="text-xs text-slate-500">
          El enlace vale una hora y solo se puede usar una vez. Si no te llega,
          acércate a admisión con tu documento.
        </p>
        <Link
          to={`${base}/login`}
          className="inline-block text-sm font-medium text-[color:var(--color-link)] hover:underline"
        >
          Volver a entrar
        </Link>
      </div>
    );
  }

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        pedir.mutate();
      }}
    >
      <div className="text-center">
        <KeyRound className="mx-auto h-9 w-9 text-[color:var(--color-link)]" />
        <h1 className="mt-2 text-lg font-semibold text-slate-900">
          Recuperar mi contraseña
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Te mandamos un enlace al correo que tengas registrado.
        </p>
      </div>

      {pedir.isError && (
        <Alert>{formatApiError(pedir.error, "No se pudo enviar. Intenta otra vez.")}</Alert>
      )}

      <Field
        label="Tu correo o tu documento"
        htmlFor="ident"
        hint="Lo que recuerdes. Si eres paciente, tu DNI."
      >
        <Input
          id="ident"
          value={identificador}
          onChange={(e) => setIdentificador(e.target.value)}
          placeholder="correo@ejemplo.com o 40596950"
          autoFocus
        />
      </Field>

      <Button
        type="submit"
        className="w-full"
        loading={pedir.isPending}
        disabled={identificador.trim().length < 3}
      >
        Enviarme el enlace
      </Button>

      <Link
        to={`${base}/login`}
        className="block text-center text-sm text-slate-500 hover:underline"
      >
        Volver
      </Link>
    </form>
  );
}

function ElegirNueva({ token }: { token: string }) {
  const base = useBase();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [repetida, setRepetida] = useState("");

  const confirmar = useMutation({
    mutationFn: () => confirmarRecuperacion(token, password),
    onSuccess: () => navigate(`${base}/login`, { replace: true }),
  });

  // Se comprueba aquí y no al enviar: enterarse de que las dos no coinciden
  // después de mandarlas es rellenar el formulario otra vez.
  const noCoinciden = repetida.length > 0 && repetida !== password;
  const valida = password.length >= 8 && password === repetida;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (valida) confirmar.mutate();
      }}
    >
      <div className="text-center">
        <KeyRound className="mx-auto h-9 w-9 text-[color:var(--color-link)]" />
        <h1 className="mt-2 text-lg font-semibold text-slate-900">
          Elige tu contraseña nueva
        </h1>
      </div>

      {confirmar.isError && (
        <Alert>
          {formatApiError(confirmar.error, "Este enlace ya no vale. Pide uno nuevo.")}
        </Alert>
      )}

      <Field label="Contraseña nueva" htmlFor="pwd" hint="Al menos 8 caracteres.">
        <Input
          id="pwd"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
      </Field>

      <Field
        label="Repítela"
        htmlFor="pwd2"
        hint={noCoinciden ? "No coinciden." : undefined}
      >
        <Input
          id="pwd2"
          type="password"
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
        />
      </Field>

      <Button
        type="submit"
        className="w-full"
        loading={confirmar.isPending}
        disabled={!valida}
      >
        Guardar y entrar
      </Button>

      <Link
        to={`${base}/recuperar`}
        className="block text-center text-sm text-slate-500 hover:underline"
      >
        Pedir otro enlace
      </Link>
    </form>
  );
}
